import { getCurrentOrgSlug } from '@shared/utils/get-org-id';
import qs from 'qs';

export const relativePath = (path: string) => path.substring(1);

export const createRoute = (
  path:
    | (typeof PATHS)[keyof typeof PATHS]
    | (typeof PUBLIC_PATH)[keyof typeof PUBLIC_PATH],
  orgSlug?: string | null,
  params?: Record<string, unknown>
) => {
  const currentOrgSlug = orgSlug || getCurrentOrgSlug();

  const queryString = qs.stringify(params, { addQueryPrefix: true });

  return `/${currentOrgSlug}${path}${queryString}`;
};

export const createNestedRoute = (path: string, { depth = 1 } = {}) => {
  const parts = path.split('/').filter(Boolean);
  const updatedParts = parts.slice(depth);
  return updatedParts.join('/');
};

export const PUBLIC_PATH = {
  ROOT: '/',
  NOT_FOUND: '/not-found',
  UNAUTHORIZED: '/unauthorized',
  REGISTER: '/register',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  CHECK_MAIL: '/check-mail',
  VERIFICATION: '/verification',
  PLANS_AND_PRICING: '/plans-and-pricing',
  PLANS_AND_SUB_DETAILS: '/plans-and-subscription-details',
};

export const PATHS = {
  // Dashboard
  DASHBOARDS: '/dashboards',

  //   LEADS_DASHBOARD: "/dashboards/leads",
  //   PARTICIPANTS_DASHBOARD: "/dashboards/participants",
  //   FINACIAL_DASHBOARD: "/dashboards/financial",
  //   TASKS_DASHBOARD: "/dashboards/tasks",

  // Programs
  PROGRAMS: '/programs',
  ADD_PROGRAM: '/programs/new',
  EDITPROGRAM(id: string | number = ':id') {
    return `/programs/${id}`;
  },

  // Reminders (hub + tab routes, same style as /leads + /leads/follow-ups)
  REMINDERS: '/reminders',
  REMINDERDASHBOARD: '/reminders/dashboard',
  REMINDERS_OVERVIEW: '/reminders/overview',
  REMINDERS_ALL: '/reminders/all-reminders',
  REMINDERS_TEMPLATES: '/reminders/templates',

  // Placements
  PLACEMENTS: '/placements',
  HOST_COMPANIES: '/host-companies',
  PLACEMENT(id: string | number = ':id') {
    return `/placements/${id}`;
  },
  CREATE_PLACEMENT: '/placements/new',
  EDIT_PLACEMENT(id: string | number = ':id') {
    return `/placements/edit/${id}`;
  },

  // Commission
  COMMISSION: '/commission',

  // Tasks
  TASKS: '/tasks',

  // users
  USERS: '/users',
  CREATE_USER: '/users/create',
  EDIT_USER: '/users/edit',
  ROLES: '/users/roles',
  SINGLE_USER: '/users/:userId',
  SINGLE_PARTNER: '/users/partner/:partnerId',
  SINGLE_PARTNER_EDIT: '/users/partner/:partnerId/edit',
  USER_EDIt: '/users/:userId/edit',
  BANK_EDIT: '/users/:userId/bank-edit',
  ROLE(id: string | number = ':id') {
    return `/roles/${id}`;
  },
  EDIT_ROLE(id: string | number = ':id') {
    return `/roles/edit/${id}`;
  },

  // Accounting
  INVOICES: '/invoices',
  ACCOUNTING_INVOICES: '/accounting/invoices',
  CREATE_INVOICE: '/accounting/invoices/create',
  EDIT_INVOICE: '/accounting/invoices/edit/:id',

  ACCOUNTING_PAYMENTS: '/accounting/payments',
  CREATE_PAYMENT: '/accounting/payments/create',
  EDIT_PAYMENT: '/accounting/payments/edit/:id',

  ACCOUNTING_PRODUCTS: '/accounting/products',
  CREATE_PRODUCT: '/accounting/products/create',
  EDIT_PRODUCT: '/accounting/products/edit/:id',

  // Participants
  PARTICIPANTS: '/participants',
  ALL_PARTICIPANTS: '/participants/all',
  STAGES: '/participants/stages',
  PARTICIPANTS_PLACEMENTS: '/participants/placements',

  SINGLE_SUBMISSION(id: string | number = ':submissionId') {
    return `/submissions/${id}`;
  },
  EDIT_PARTICIPANT(id: string | number = ':participantId') {
    return `/participants/edit/${id}`;
  },

  // Leads
  LEADS: '/leads',
  LEADS_FOLLOW_UPS: '/leads/follow-ups',
  SINGLE_LEAD: '/leads/:leadId',
  LEAD_MAIN_INFO_EDIT: '/leads/:leadId/main-info',

  // Settings
  SETTINGS: '/settings',
  EDIT_ORG_INFO: '/settings/:orgId/edit',
  SETTINGS_COMPANY_INFO: '/settings/company-info',
  SETTINGS_BANK_INFO: '/settings/bank-info',
  SETTINGS_PREFERENCES: '/settings/preferences',
  SETTINGS_BILLING_A_SUBSCRIPTION: '/settings/billing-and-subscription',
  SETTINGS_BRANCHES: '/settings/branches',
  SETTINGS_INTEGRATION: '/settings/integration',
  SETTINGS_KNOWLEDGE_BASE: '/settings/knowledge-base',
  SETTINGS_AI_AGENTS: '/settings/ai-agents',
  SETTINGS_BRAND_THEME: '/settings/brand-theme',
  SETTINGS_BANK_INFO_EDIT(id: string | number = ':bankId') {
    return `/settings/bank-info/${id}`;
  },
  SETTINGS_BANK_INFO_NEW: '/settings/bank-info/new',

  // MyProfile Settings
  SETTINGS_PROFILE_SETTINGS: '/settings/profile-settings',
  EDIT_MY_INFO: (staffId?: number) => {
    if (staffId) return `/settings/profile-settings/${staffId}/edit`;
    return '/settings/profile-settings/:staffId/edit';
  },
  SETTINGS_PROFILE_ACTIVITYLOGS: '/settings/profile-settings/activity-logs',
  SETTINGS_PROFILE_TASKS: '/settings/profile-settings/my-tasks',
  SETTINGS_PROFILE_DOCUMENTS: '/settings/profile-settings/my-documents',
  SETTINGS_PROFILE_BANK_INFOS: '/settings/profile-settings/my-bank-infos',
  SETTINGS_PROFILE_EMPLOYMENT_INFOS:
    '/settings/profile-settings/my-employment-infos',

  //knowladge center
  KNOWLEDGECENTER: '/knowledge-center',
  Notifications: '/notifications',
  UNIVERSITIES: '/universities',
  ANNOUNCEMENTS: '/announcements',

  // Conversations (omnichannel inbox — read-only view of the AI agent's
  // WhatsApp/Instagram/Facebook conversations with customers)
  CONVERSATIONS: '/conversations',
  CONVERSATION_DETAIL(id: string | number = ':conversationId') {
    return `/conversations/${id}`;
  },
  CONVERSATIONS_ONBOARDING: '/conversations/onboarding',
  CONVERSATIONS_CHANNELS: '/conversations/channels',
  // Knowledge bases opened from the inbox omni rail, same conversations shell
  CONVERSATIONS_KNOWLEDGE_BASE: '/conversations/knowledge-base',
  CONVERSATIONS_KNOWLEDGE_BASE_DETAIL(id: string | number = ':kbaseId') {
    return `/conversations/knowledge-base/${id}`;
  },
  CONVERSATIONS_AGENTS: '/conversations/agents',
  // The contact book, opened from the inbox omni rail
  CONVERSATIONS_CONTACTS: '/conversations/contacts',
  // Automations, opened from the inbox omni rail
  CONVERSATIONS_AUTOMATION: '/conversations/automation',
  CONVERSATIONS_AUTOMATION_ACTIVITY(id: string | number = ':automationId') {
    return `/conversations/automation/${id}/activity`;
  },
  CONVERSATIONS_AGENTS_DETAIL(id: string | number = ':agentId') {
    return `/conversations/agents/${id}`;
  },

  // Contacts (the inbox-fed contact book) and their message templates
  CONTACTS: '/contacts',
  CONTACT_DETAIL(id: string | number = ':contactId') {
    return `/contacts/${id}`;
  },
  MESSAGE_TEMPLATES: '/message-templates',

  // Knowledge bases: what the AI agents answer customers from
  KNOWLEDGE_BASE: '/knowledge-base',
  KNOWLEDGE_BASE_DETAIL(id: string | number = ':kbaseId') {
    return `/knowledge-base/${id}`;
  },

  // Old Automation links, kept only as redirects into the Conversations
  // shell's Automation tab and its drawers.
  AUTOMATIONS: '/automations',
  AUTOMATION_NEW: '/automations/new',
  AUTOMATION_DETAIL(id: string | number = ':automationId') {
    return `/automations/${id}`;
  },
  AUTOMATION_EDIT(id: string | number = ':automationId') {
    return `/automations/${id}/edit`;
  },
} as const;
